import { useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { invalidatePlayerCache, invalidateAllCache } from '../utils/optimizedDataFetching';
import { useUser } from './useUser';

/**
 * Hook to manage real-time updates for bowling data
 */
export function useRealtimeUpdates() {
  const { selectedPlayer } = useUser();

  const handleDataChange = useCallback((payload: any) => {
    console.log('Real-time data change detected:', payload);
    
    // Invalidate relevant cache based on the change
    if (payload.table === 'league_bowler_week_stats') {
      const bowlerId = payload.new?.bowler_id || payload.old?.bowler_id;
      if (bowlerId) {
        invalidatePlayerCache(bowlerId);
        
        // If this is the currently selected player, trigger a refresh
        if (selectedPlayer && selectedPlayer.bowler_id === bowlerId) {
          // Dispatch custom event to trigger data refresh
          window.dispatchEvent(new CustomEvent('playerDataUpdated', { 
            detail: { bowlerId } 
          }));
        }
      }
    }
    
    // For other tables that might affect league averages
    if (payload.table === 'league_bowlers' || payload.table === 'league_games') {
      invalidateAllCache();
      window.dispatchEvent(new CustomEvent('leagueDataUpdated'));
    }
  }, [selectedPlayer]);

  useEffect(() => {
    // Subscribe to changes in weekly stats
    const weeklyStatsSubscription = supabase
      .channel('weekly_stats_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'league_bowler_week_stats'
        },
        handleDataChange
      )
      .subscribe();

    // Subscribe to changes in bowler data
    const bowlersSubscription = supabase
      .channel('bowlers_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'league_bowlers'
        },
        handleDataChange
      )
      .subscribe();

    // Subscribe to changes in game data
    const gamesSubscription = supabase
      .channel('games_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'league_games'
        },
        handleDataChange
      )
      .subscribe();

    return () => {
      weeklyStatsSubscription.unsubscribe();
      bowlersSubscription.unsubscribe();
      gamesSubscription.unsubscribe();
    };
  }, [handleDataChange]);

  return {
    // Expose method to manually trigger cache invalidation
    invalidateCache: invalidateAllCache,
    invalidatePlayerCache: (bowlerId: number) => invalidatePlayerCache(bowlerId),
  };
}

/**
 * Hook to listen for real-time data updates and trigger refetch
 */
export function useRealtimeRefresh(refetchCallback: () => void, dependencies: any[] = []) {
  useEffect(() => {
    const handlePlayerDataUpdate = () => {
      refetchCallback();
    };

    const handleLeagueDataUpdate = () => {
      refetchCallback();
    };

    window.addEventListener('playerDataUpdated', handlePlayerDataUpdate);
    window.addEventListener('leagueDataUpdated', handleLeagueDataUpdate);

    return () => {
      window.removeEventListener('playerDataUpdated', handlePlayerDataUpdate);
      window.removeEventListener('leagueDataUpdated', handleLeagueDataUpdate);
    };
  }, [refetchCallback, ...dependencies]);
}